import { useEffect, useState, useCallback } from "react";
import api, { apiErr, downloadFile } from "@/lib/api";
import { useWorkspace } from "@/context/WorkspaceContext";
import { useAuth } from "@/context/AuthContext";
import { STANDARDS } from "@/constants/testIds";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { fmtDate } from "@/lib/format";
import {
  BadgeCheck, ShieldCheck, ShieldAlert, Download, Sparkles, CheckCircle2, AlertTriangle,
  MinusCircle, Loader2, Info, ChevronDown, Calculator, Database, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { ProvenanceList } from "@/components/ProvenancePanel";
import LiasScorecard from "@/components/LiasScorecard";
import LeafScorecard from "@/components/LeafScorecard";
import LstpScorecard from "@/components/LstpScorecard";

const STATUS = {
  covered: { label: "Covered", cls: "bg-emerald-100 text-emerald-900 border-emerald-300", Icon: CheckCircle2, bar: "bg-forest-mid" },
  partial: { label: "Partial", cls: "bg-amber-100 text-amber-900 border-amber-300", Icon: AlertTriangle, bar: "bg-amber-400" },
  gap: { label: "Gap", cls: "bg-rose-100 text-rose-900 border-rose-300", Icon: AlertTriangle, bar: "bg-rose-400" },
  na: { label: "N/A", cls: "bg-slate-100 text-slate-600 border-slate-200", Icon: MinusCircle, bar: "bg-slate-300" },
};
const readyColor = (v) => (v == null ? "text-slate-400" : v >= 80 ? "text-forest-mid" : v >= 50 ? "text-amber-600" : "text-rose-600");

function Chip({ children, className = "", ...p }) {
  return <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-semibold ${className}`} {...p}>{children}</span>;
}

function SegmentBar({ counts }) {
  const total = counts.total || 1;
  const seg = (k) => `${((counts[k] || 0) / total) * 100}%`;
  return (
    <div className="flex h-2 w-full overflow-hidden rounded-full bg-slate-100">
      {["covered", "partial", "gap", "na"].map((k) => (
        <div key={k} className={STATUS[k].bar} style={{ width: seg(k) }} />
      ))}
    </div>
  );
}

function FrameworkCard({ f, onOpen }) {
  const c = f.counts;
  return (
    <Card
      className="cursor-pointer border-sage-light p-4 transition-shadow hover:shadow-md"
      data-testid={STANDARDS.card(f.key)}
      onClick={() => onOpen(f.key)}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate font-heading text-base font-bold text-forest-dark">{f.name}</p>
          <p className="truncate text-[11px] text-muted-foreground">{f.short}</p>
        </div>
        {f.ai_assist?.accepted && (
          <Chip className="shrink-0 border-forest-dark bg-forest-mid text-white"><Sparkles className="h-3 w-3" /> AI note</Chip>
        )}
      </div>
      <div className="mt-3 flex items-end gap-2">
        <span className={`font-heading text-4xl font-bold ${readyColor(f.readiness)}`}>
          {f.readiness == null ? "—" : `${Math.round(f.readiness)}%`}
        </span>
        <span className="mb-1.5 text-[11px] text-muted-foreground">readiness</span>
      </div>
      <div className="mt-2"><SegmentBar counts={c} /></div>
      <div className="mt-2 flex flex-wrap gap-1.5 text-[11px]">
        <Chip className={STATUS.covered.cls}>{c.covered} covered</Chip>
        <Chip className={STATUS.partial.cls}>{c.partial} partial</Chip>
        <Chip className={STATUS.gap.cls}>{c.gap} gaps</Chip>
      </div>
      <p className="mt-2 truncate text-[10px] uppercase tracking-wider text-muted-foreground">{f.authority}</p>
    </Card>
  );
}

function AiNote({ periodId, fw, canGenerate, canAccept, onChange }) {
  const [busy, setBusy] = useState(false);
  const ai = fw.ai_assist;

  const generate = async () => {
    setBusy(true);
    try {
      await api.post(`/score/periods/${periodId}/standards/${fw.key}/ai-assist`);
      toast.success("AI alignment note drafted for review");
      await onChange();
    } catch (e) { toast.error(apiErr(e)); }
    setBusy(false);
  };
  const accept = async () => {
    try {
      await api.post(`/score/periods/${periodId}/standards/${fw.key}/ai-assist/accept`);
      toast.success("Alignment note accepted");
      await onChange();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const discard = async () => {
    try {
      await api.post(`/score/periods/${periodId}/standards/${fw.key}/ai-assist/discard`);
      toast("Draft discarded");
      await onChange();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <Card className="border-sage-light bg-sage-surface p-4" data-testid="standards-ai-card">
      <div className="flex items-center justify-between gap-2">
        <p className="flex items-center gap-1.5 text-sm font-semibold text-forest-dark">
          <Sparkles className="h-4 w-4 text-forest-mid" /> AI alignment note
          <span className="text-[11px] font-normal text-muted-foreground">· human-reviewed · never a source of numbers</span>
        </p>
        {canGenerate && (
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={generate} disabled={busy} data-testid={STANDARDS.aiGenerate}>
            {busy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Sparkles className="mr-1 h-3.5 w-3.5" />}
            {ai ? "Regenerate" : "Draft with AI"}
          </Button>
        )}
      </div>
      {!ai ? (
        <p className="mt-2 text-xs text-muted-foreground">No AI note yet. {canGenerate ? "Draft one from the issued evidence above." : "Awaiting an internal reviewer."}</p>
      ) : (
        <>
          <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-forest-dark">{ai.draft}</p>
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-sage-light pt-2">
            <p className="text-[11px] text-muted-foreground">
              Drafted by {ai.model || "automatic summary"} · requested by {ai.generated_by_name}
              {ai.accepted && <span className="ml-1 font-semibold text-forest-mid">· accepted by {ai.accepted_by_name}</span>}
            </p>
            <div className="flex gap-2">
              {canAccept && !ai.accepted && (
                <Button size="sm" className="h-7 bg-forest-mid text-xs hover:bg-forest-dark" onClick={accept} data-testid={STANDARDS.aiAccept}>Accept</Button>
              )}
              {canGenerate && (
                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={discard} data-testid={STANDARDS.aiDiscard}>Discard</Button>
              )}
            </div>
          </div>
        </>
      )}
    </Card>
  );
}

function KpiCalc({ k, periodId, navigate }) {
  return (
    <div className="rounded-md border border-sage-light bg-white p-2.5" data-testid={STANDARDS.kpiCalc(k.code)}>
      <div className="flex items-start justify-between gap-2">
        <p className="flex items-center gap-1.5 text-xs font-semibold text-forest-dark">
          <Calculator className="h-3.5 w-3.5 text-forest-mid" />
          <span className="font-mono">{k.code}</span> {k.name}
          {k.pillar_label && <span className="font-normal text-muted-foreground">· {k.pillar_label}</span>}
        </p>
        <button type="button" onClick={() => navigate(`/project?tab=score&period=${periodId}&kpi=${k.code}`)}
          className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap text-[11px] font-semibold text-forest-mid hover:text-forest-dark"
          data-testid={STANDARDS.kpiJump(k.code)}>
          View in ROOTS Score <ExternalLink className="h-3 w-3" />
        </button>
      </div>
      {k.status === "scored" ? (
        <div className="mt-1.5 space-y-1 text-[11px] text-muted-foreground">
          <p><b className="text-forest-dark">Method:</b>{" "}
            {k.transform === "proportion"
              ? `Proportion — passed ÷ required × 100 (${k.unit})`
              : `Linear ${k.transform === "lower" ? "(lower is better)" : "(higher is better)"} — anchors ${k.anchor0}→0, ${k.anchor100}→100 (${k.unit})`}
          </p>
          <p><b className="text-forest-dark">Input:</b>{" "}
            {k.transform === "proportion" ? `${k.passed} of ${k.required}` : `${k.raw_value} ${k.unit}`}
            {" → "}<b className="text-forest-dark">KPI score {k.score}</b>
          </p>
          {k.score_steps?.length > 0 && (
            <ul className="space-y-0.5 rounded bg-sage-surface p-2 font-mono text-[10px] leading-relaxed text-forest-dark">
              {k.score_steps.map((s, j) => <li key={j}>{s}</li>)}
            </ul>
          )}
          <p><b className="text-forest-dark">Weight:</b> effective {k.effective_weight}{k.context_multiplier > 1 ? ` · context ×${k.context_multiplier}` : ""}</p>
          {k.weight_steps && <p className="font-mono text-[10px]">{k.weight_steps}</p>}
          <p><b className="text-forest-dark">Contribution to overall score:</b> {k.contribution_display} pts · evidence quality Q{k.evidence_quality ?? 0}</p>
        </div>
      ) : k.status === "excluded" ? (
        <p className="mt-1 text-[11px] text-slate-500">Excluded — reviewer-approved Not Applicable{k.exclusion_reason ? `: ${k.exclusion_reason}` : ""}. Drops out of this disclosure's applicability.</p>
      ) : (
        <p className="mt-1 text-[11px] text-rose-700">Not calculable{k.error_reason ? ` — ${String(k.error_reason).replace(/_/g, " ").toLowerCase()}` : ""}. No contribution to the score.</p>
      )}
    </div>
  );
}

function DisclosureRow({ d, i, periodId, navigate }) {
  const [open, setOpen] = useState(false);
  const [prov, setProv] = useState({});
  const s = STATUS[d.status] || STATUS.na;

  const openRecord = (l) => {
    const lk = l.link || {};
    if (lk.area === "setup") navigate(`/setup?tab=${lk.tab}&record=${lk.record_id}`);
    else navigate(`/project?tab=${lk.tab || "work-orders"}`);
  };

  const toggle = async () => {
    const nv = !open;
    setOpen(nv);
    if (nv && Object.keys(prov).length === 0) {
      const entries = await Promise.all(d.kpis.map(async (k) => {
        try { const r = await api.get(`/score/periods/${periodId}/entries/${k.code}/provenance`); return [k.code, r.data]; }
        catch { return [k.code, null]; }
      }));
      setProv(Object.fromEntries(entries));
    }
  };

  const st = d.stats || {};
  const logic = d.status === "na"
    ? `All ${st.total} mapped KPIs are reviewer-approved Not Applicable → N/A.`
    : `${st.scored} of ${st.applicable} applicable KPIs evidenced (avg ${st.eff_score ?? "—"}/100${st.excluded ? `, ${st.excluded} excluded N/A` : ""}) → ${s.label}. Covered = all mapped KPIs strong (≥78); Partial = evidenced but some KPIs developing/weak; Gap = weak evidence (<55).`;

  return (
    <div className="rounded-lg border border-sage-light p-3" data-testid={STANDARDS.disclosureRow(i)}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-forest-dark"><span className="font-mono text-xs text-muted-foreground">{d.ref}</span> {d.title}</p>
          <p className="text-[11px] text-muted-foreground">{d.description}</p>
        </div>
        <Chip className={`${s.cls} shrink-0`}><s.Icon className="h-3 w-3" /> {s.label}</Chip>
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {d.kpis.map((k) => {
          const cls = k.status === "excluded" ? "border-slate-200 bg-slate-50 text-slate-500"
            : k.strength === "strong" ? "border-emerald-200 bg-emerald-50 text-emerald-800"
              : k.strength === "developing" ? "border-amber-200 bg-amber-50 text-amber-800"
                : "border-rose-200 bg-rose-50 text-rose-700";
          return (
            <span key={k.code} className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[11px] ${cls}`}>
              <span className="font-mono">{k.code}</span> {k.name}
              {k.score != null && <span className="font-semibold">· {k.score}</span>}
            </span>
          );
        })}
      </div>
      {d.recommendation && (
        <p className="mt-2 flex items-start gap-1 rounded bg-amber-50 p-1.5 text-[11px] text-amber-900">
          <Info className="mt-0.5 h-3 w-3 shrink-0" /> {d.recommendation}
        </p>
      )}
      <button onClick={toggle} className="mt-2 flex items-center gap-1 text-[11px] font-semibold text-forest-mid hover:text-forest-dark" data-testid={STANDARDS.discExpand(i)}>
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} /> {open ? "Hide" : "Explain"} calculation &amp; data source
      </button>
      {open && (
        <div className="mt-2 space-y-3 border-t border-sage-light pt-3" data-testid={STANDARDS.discDetail(i)}>
          <p className="rounded bg-sage-surface p-2 text-[11px] text-forest-dark"><b>How this status is derived:</b> {logic}</p>
          {d.kpis.map((k) => (
            <div key={k.code} className="space-y-2 rounded-lg border border-sage-light bg-sage-surface/40 p-2.5">
              <KpiCalc k={k} periodId={periodId} navigate={navigate} />
              <div>
                <p className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-forest-dark"><Database className="h-3.5 w-3.5 text-forest-mid" /> Data source</p>
                <ProvenanceList prov={prov[k.code]} onOpenRecord={openRecord} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function LushStatusChip({ status }) {
  const met = status === "met";
  return (
    <Chip className={met ? "border-emerald-300 bg-emerald-100 text-emerald-900" : "border-amber-300 bg-amber-100 text-amber-900"}>
      {met ? <CheckCircle2 className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />} {met ? "Met" : "Attention"}
    </Chip>
  );
}

function LushPanel({ periodId }) {
  const [sites, setSites] = useState(null);
  useEffect(() => {
    if (!periodId) return;
    let active = true;
    api.get(`/score/periods/${periodId}/lush`)
      .then((r) => { if (active) setSites(r.data.sites || []); })
      .catch(() => { if (active) setSites([]); });
    return () => { active = false; };
  }, [periodId]);

  if (!sites) return <p className="text-xs text-muted-foreground">Loading URA LUSH compliance…</p>;
  if (sites.length === 0) return null;

  return (
    <div data-testid={STANDARDS.lushPanel}>
      <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        <ShieldCheck className="h-3.5 w-3.5 text-forest-mid" /> URA LUSH compliance by site
      </p>
      <div className="space-y-3">
        {sites.map((s) => (
          <div key={s.site_id} data-testid={STANDARDS.lushSite(s.site_id)} className="rounded-lg border border-sage-light p-3">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-forest-dark">{s.site_name}</p>
                <p className="text-[11px] text-muted-foreground">
                  {s.strategic_area ? "Strategic Area" : `Outside Strategic Areas · Gross Plot Ratio ${s.gross_plot_ratio}`}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <span className={`font-heading text-xl font-bold ${readyColor(s.readiness)}`}>{s.readiness}%</span>
                <LushStatusChip status={s.overall_status} />
              </div>
            </div>
            <div className="mt-2 divide-y divide-sage-light overflow-hidden rounded-md border border-sage-light">
              {s.metrics.map((m) => (
                <div key={m.key} data-testid={STANDARDS.lushMetric(s.site_id, m.key)} className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 px-2.5 py-1.5 text-xs">
                  <div className="min-w-0">
                    <p className="font-medium text-forest-dark">{m.label}</p>
                    <p className="text-[10px] text-muted-foreground">{m.note}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="whitespace-nowrap text-muted-foreground">
                      target {m.target}{m.unit} · <b className="text-forest-dark">actual {m.actual}{m.unit}</b>
                    </span>
                    <LushStatusChip status={m.status} />
                  </div>
                </div>
              ))}
              <div className="bg-sage-surface/40 px-2.5 py-1.5 text-[11px] text-muted-foreground">
                Vertical greenery &amp; rooftop greening: <b className="text-forest-dark">{s.vertical_rooftop.actual}%</b> provided · {s.vertical_rooftop.note}
              </div>
            </div>
          </div>
        ))}
      </div>
      <p className="mt-2 text-[11px] text-muted-foreground">
        Illustrative site data for the demo — a readiness aid, not a certification or an approved submission.
      </p>
    </div>
  );
}

function DetailModal({ periodId, fw, header, canGenerate, canAccept, onClose, onChange }) {
  const navigate = useNavigate();
  if (!fw) return null;
  const download = () =>
    downloadFile(`/score/periods/${periodId}/standards/${fw.key}/report.pdf`, `roots-standards-${fw.key}.pdf`)
      .catch(() => toast.error("PDF export failed"));

  return (
    <Dialog open={!!fw} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto p-0" data-testid={STANDARDS.detailModal}>
        <DialogHeader className="sticky top-0 z-10 flex flex-row items-center justify-between border-b bg-white px-5 py-3">
          <DialogTitle className="flex items-center gap-2 font-heading text-base">
            <ShieldCheck className="h-4 w-4 text-forest-mid" /> {fw.name}
          </DialogTitle>
          <DialogDescription className="sr-only">Disclosure mapping, alignment readiness and per-KPI calculation and data source for {fw.name}.</DialogDescription>
          <div className="mr-6 flex items-center gap-2">
            {fw.key !== "lias_obc" && fw.key !== "nparks_leaf" && fw.key !== "nparks_lstp" && (
              <Button size="sm" variant="outline" onClick={download} data-testid={STANDARDS.downloadPdf}><Download className="mr-1 h-4 w-4" /> PDF</Button>
            )}
          </div>
        </DialogHeader>

        <div className="space-y-4 p-6">
          {fw.key === "lias_obc" ? <LiasScorecard periodId={periodId} /> : fw.key === "nparks_leaf" ? <LeafScorecard periodId={periodId} /> : fw.key === "nparks_lstp" ? <LstpScorecard periodId={periodId} /> : (<>
          <div className="rounded-xl border border-sage-light bg-sage-surface p-4">
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{fw.short} · {fw.authority}</p>
            <div className="mt-2 flex flex-wrap items-end gap-3">
              <span className={`font-heading text-5xl font-bold ${readyColor(fw.readiness)}`}>
                {fw.readiness == null ? "—" : `${Math.round(fw.readiness)}%`}
              </span>
              <span className="mb-2 text-sm text-muted-foreground">alignment readiness</span>
            </div>
            <div className="mt-2"><SegmentBar counts={fw.counts} /></div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              <Chip className={STATUS.covered.cls}>{fw.counts.covered} covered</Chip>
              <Chip className={STATUS.partial.cls}>{fw.counts.partial} partial</Chip>
              <Chip className={STATUS.gap.cls}>{fw.counts.gap} gaps</Chip>
              <Chip className={STATUS.na.cls}>{fw.counts.na} N/A</Chip>
              {fw.avg_kpi_score != null && <Chip className="border-sage-light bg-white text-forest-dark">Avg KPI {fw.avg_kpi_score}</Chip>}
            </div>
            <p className="mt-2 text-[11px] text-muted-foreground">
              From issued score {header?.overall_display ?? "—"}/100 ({header?.band_label}) · {header?.contract_name} · {header?.period_label}
            </p>
          </div>

          <AiNote periodId={periodId} fw={fw} canGenerate={canGenerate} canAccept={canAccept} onChange={onChange} />

          {fw.key === "ura_lush" && <LushPanel periodId={periodId} />}

          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Disclosure mapping</p>
            <div className="space-y-2">
              {fw.disclosures.map((d, i) => (
                <DisclosureRow key={d.ref} d={d} i={i} periodId={periodId} navigate={navigate} />
              ))}
            </div>
          </div>

          <p className="border-t border-sage-light pt-3 text-[11px] text-muted-foreground">
            Prepared by ROOTS as a readiness aid. This is not a certification or an assured disclosure — every KPI is reproducible from the issued ROOTS evidence.
          </p>
          </>)}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function StandardsPage() {
  const ws = useWorkspace();
  const { hasRole } = useAuth();
  const [periods, setPeriods] = useState([]);
  const [sel, setSel] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openKey, setOpenKey] = useState(null);

  const loadPeriods = useCallback(async () => {
    if (!ws.contractId) { setPeriods([]); setSel(""); setData(null); return; }
    try {
      const r = await api.get(`/score/periods?contract_id=${ws.contractId}`);
      const issued = (r.data || []).filter((p) => p.status === "issued");
      setPeriods(issued);
      setSel(issued[0]?.id || "");
      if (!issued.length) setData(null);
    } catch (e) { toast.error(apiErr(e)); }
  }, [ws.contractId]);

  const loadStandards = useCallback(async (periodId) => {
    if (!periodId) { setData(null); return; }
    setLoading(true);
    try {
      const r = await api.get(`/score/periods/${periodId}/standards`);
      setData(r.data);
    } catch (e) { toast.error(apiErr(e)); }
    setLoading(false);
  }, []);

  useEffect(() => { loadPeriods(); }, [loadPeriods]);
  useEffect(() => { loadStandards(sel); }, [sel, loadStandards]);

  const openFw = data?.frameworks?.find((f) => f.key === openKey) || null;

  if (!hasRole("tenant_admin", "contract_manager", "supervisor", "finance_reviewer", "client_rep", "auditor")) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center" data-testid={STANDARDS.root}>
        <Card className="max-w-sm border-sage-light p-8 text-center">
          <ShieldAlert className="mx-auto h-8 w-8 text-rose-500" />
          <p className="mt-3 font-heading text-lg font-semibold text-forest-dark">Not authorised</p>
          <p className="mt-1 text-sm text-muted-foreground">Standards &amp; Compliance packs are for internal reporting and client roles. Ask your administrator if you need access.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid={STANDARDS.root}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-heading text-2xl font-bold text-forest-dark">
            <BadgeCheck className="h-6 w-6 text-forest-mid" /> Standards & Compliance
          </h1>
          <p className="text-sm text-muted-foreground">
            Map an <b>issued</b> ROOTS assessment to reporting frameworks — readiness, gaps and branded packs
          </p>
        </div>
        {periods.length > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Issued period</span>
            <Select value={sel} onValueChange={setSel}>
              <SelectTrigger className="h-9 w-[180px]" data-testid={STANDARDS.periodSelect}><SelectValue placeholder="Period" /></SelectTrigger>
              <SelectContent>
                {periods.map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      {!ws.contractId ? (
        <Card className="border-dashed border-sage-light p-10 text-center text-sm text-muted-foreground">
          Select a contract in the bar above to view its standards packs.
        </Card>
      ) : periods.length === 0 ? (
        <Card className="border-dashed border-sage-light p-10 text-center text-sm text-muted-foreground">
          No issued ROOTS assessment for this contract yet. Issue a score from the Project Record · ROOTS Score workspace to generate standards packs.
        </Card>
      ) : loading && !data ? (
        <p className="text-sm text-muted-foreground">Preparing standards packs…</p>
      ) : data ? (
        <>
          <div className="flex items-start gap-2 rounded-lg border-l-4 border-forest-mid bg-sage-surface p-3">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-forest-mid" />
            <p className="text-xs text-forest-dark">
              Each pack maps the issued ROOTS KPIs for <b>{data.header?.contract_name}</b> ({data.header?.period_label}) to a framework's disclosures.
              Readiness reflects evidence coverage — it is a preparation aid, <b>not</b> a certification. ROOTS numbers are the single source of truth.
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {data.frameworks.map((f) => <FrameworkCard key={f.key} f={f} onOpen={setOpenKey} />)}
          </div>
        </>
      ) : null}

      <DetailModal
        periodId={sel}
        fw={openFw}
        header={data?.header}
        canGenerate={!!data?.can_generate}
        canAccept={!!data?.can_accept}
        onClose={() => setOpenKey(null)}
        onChange={() => loadStandards(sel)}
      />
    </div>
  );
}
